import React, { RefObject, useEffect, useRef, useState } from 'react';
import cn from 'clsx';
import sharedStyles from '../styles.module.scss';
import styles from './styles.module.scss';
import Button from '@components-clean/core/button';
import { ChipData } from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import Tooltip from '@components-clean/core/tooltip';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';

type Props = {
    onClick: (chip: ChipData) => void;
    filterChipElements: RefObject<Map<number, HTMLElement>>;
};

const BACK_BUTTON_KEY = -1;
const FORWARD_BUTTON_KEY = -2;

export const FilterChips = ({ onClick, filterChipElements }: Props) => {
    const { chipData } = useStudyDataFilterContext();

    const [leftDisabled, setLeftDisabled] = useState<boolean>(true);
    const [rightDisabled, setRightDisabled] = useState<boolean>(true);
    const ulRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        if (!ulRef.current) return;
        ulRef.current.scrollBy({ left: ulRef.current.scrollWidth * -1 });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setLeftDisabled(true);
        setRightDisabled(ulRef.current.clientWidth <= ulRef.current.scrollWidth);

        const observer = new ResizeObserver((entries) => {
            const ul = entries[0];

            const { clientWidth, scrollLeft, scrollWidth } = ul.target;
            const isLeftDisabled = scrollLeft === 0;
            const isRightDisabled = clientWidth + scrollLeft === scrollWidth;
            setLeftDisabled(isLeftDisabled);
            setRightDisabled(isRightDisabled);

            // Reset the scroll to the start if both left and right are disabled
            if (isLeftDisabled && isRightDisabled) ul.target.scrollTo({ left: 0 });
        });
        observer.observe(ulRef.current);
        return () => observer.disconnect();
    }, [chipData]);

    if (!chipData.length) return null;
    return (
        <div className={cn('k-flex-auto k-ml-xs', styles.chipsWrapper)}>
            <Button
                ref={(e) => {
                    if (e?.element) filterChipElements.current.set(BACK_BUTTON_KEY, e.element);

                    return () => {
                        filterChipElements.current.delete(BACK_BUTTON_KEY);
                    };
                }}
                onClick={() => {
                    if (!ulRef.current) return;

                    const { clientWidth, scrollLeft } = ulRef.current;

                    const scrollBy = clientWidth / 2;
                    ulRef.current.scrollBy({ left: scrollBy * -1, behavior: 'smooth' });

                    setLeftDisabled(scrollLeft - scrollBy <= 0);
                    setRightDisabled(false);
                }}
                primary
                faIcon={['far', 'arrow-left']}
                faIconStyle={{ height: 15 }}
                data-testid="carousel-left"
                carousel
                className={styles.carouselNavButtons}
                disabled={leftDisabled}
            />
            <ul ref={ulRef} className={cn('k-d-flex k-gap-1 k-flex-auto', styles.filterChipsList)}>
                {chipData.map((chip) => {
                    const name = `${chip.parentName} > ${chip.category}`;
                    return (
                        <Tooltip key={chip.categoryId} position="top" anchorElement="target" parentTitle>
                            <li
                                ref={(e) => {
                                    if (e) filterChipElements.current.set(chip.categoryId, e);

                                    return () => {
                                        filterChipElements.current.delete(chip.categoryId);
                                    };
                                }}
                            >
                                <button
                                    onClick={() => onClick(chip)}
                                    type="button"
                                    className={sharedStyles.chip}
                                    title={name}
                                >
                                    <span>{name}</span>
                                </button>
                            </li>
                        </Tooltip>
                    );
                })}
            </ul>
            <Button
                ref={(e) => {
                    if (e?.element) filterChipElements.current.set(FORWARD_BUTTON_KEY, e.element);

                    return () => {
                        filterChipElements.current.delete(FORWARD_BUTTON_KEY);
                    };
                }}
                onClick={() => {
                    if (!ulRef.current) return;

                    const { clientWidth, scrollLeft, scrollWidth } = ulRef.current;

                    const scrollBy = clientWidth / 2;
                    ulRef.current.scrollBy({ left: scrollBy, behavior: 'smooth' });

                    setLeftDisabled(false);
                    setRightDisabled(clientWidth + scrollLeft + scrollBy >= scrollWidth);
                }}
                primary
                faIcon={['far', 'arrow-right']}
                faIconStyle={{ height: 15 }}
                data-testid="carousel-right"
                carousel
                className={styles.carouselNavButtons}
                disabled={rightDisabled}
            />
        </div>
    );
};
